import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RunDetail, RunSummary, controlReplacementRun, getReplacementRun, recordReplacementContact } from '../../../api/cafAutomation';
import { Drawer } from '../Overlay';
import { isClash, runSave } from '../saveEngine';
import { StepChips } from './StepChips';

export const replacementRunLabel = (run: Pick<RunSummary, 'status' | 'paused' | 'reason' | 'ending'>): string => {
  if (run.reason === 'replacement_staff_takeover') return 'Staff took over';
  if (run.paused) return 'Paused';
  if (run.status === 'unknown_external_outcome') return 'Send outcome unknown';
  if (run.status === 'pending_reconciliation') return 'Checking sent';
  if (run.status === 'held' || run.status === 'failed') return 'Needs attention';
  if (run.status === 'completed') return run.ending === 'manual' || run.ending === 'review' ? 'Manual handling' : 'Finished';
  if (run.status === 'waiting') return 'Waiting';
  return 'Working';
};

const detailLabel = (run: RunDetail) => replacementRunLabel({
  status: run.status, paused: run.paused, reason: run.reason_code, ending: run.ending,
});

const STEP_LABELS: Record<string, string> = {
  link_claim: 'Attach to the claim', ingest_claim_correspondence: 'Attach correspondence',
  record_note: 'Add a note', complete_task: 'Mark done', finish_intake_task: 'Mark done',
  archive_task: 'Archive', mark_manual: 'Mark as manual', refer_staff: 'Refer to staff',
  loa_portal: 'Send LOA', multi_rep: 'Send multi-rep email',
  cancel_confirmation: 'Send cancellation confirmation',
  forward_correspondence: 'Forward the lender email to the client',
  wait: 'Wait for the client', condition: 'Check condition', cancel_claims: 'Cancel the claim',
  stop: 'Finish', set_key_date: 'Set deadline',
};

const REASON_LABELS: Record<string, string> = {
  client_response_received: 'A client response was received. Review it before continuing.',
  workflow_paused: 'Workflow processing is paused.',
  replacement_source_changed: 'The approved source has changed. Review it before continuing.',
  replacement_source_not_approved: 'The source is no longer approved. Review its approval before continuing.',
  replacement_publication_not_ready: 'The published workflow needs review before this run can continue.',
  replacement_link_changed: 'The source workflow link has changed. Review the link before continuing.',
  replacement_definition_changed: 'The published workflow has changed. Review the publication before continuing.',
  replacement_target_changed: 'The target claim has changed. Review the current claim before continuing.',
  replacement_rule_changed: 'The cancelled claim no longer matches this rule. Review it before continuing.',
  replacement_authority_unavailable: 'Workflow authority could not be checked. Try again after it is available.',
  replacement_evidence_invalid: 'The recorded workflow evidence needs review before this run can continue.',
  claim_version_changed: 'The claim changed while this step was running. Review the current claim before continuing.',
  attachment_source_already_recorded: 'This source is already recorded on the claim. Review the existing attachment.',
  claim_staff_hold: 'Staff protection on the claim needs review before this run can continue.',
  cancellation_state_disagreement: 'The claim cancellation details need review before this run can continue.',
  cancelled_claim_review: 'The cancelled claim needs staff review before this run can continue.',
  effect_reconciliation_required: 'The send outcome needs checking before this run can continue.',
  deadline_date_unavailable: "The letter's date couldn't be worked out, so no deadline was set. Check the letter.",
  deadline_claim_cancelled: 'The claim is cancelled, so no deadline was set.',
  claim_key_dates_unavailable: "The claim's key dates couldn't be read. Check the claim before continuing.",
  reminder_identity_conflict: "Another task already uses one of these reminders. Check the claim's follow-ups.",
  provider_unknown: 'The send outcome is unknown. Check provider evidence before another send.',
  dispatch_lease_expired: 'The send outcome is unknown. Check provider evidence before another send.',
  dispatch_released: 'The send outcome is unknown. Check provider evidence before another send.',
  template_review_required: 'The email template needs review before this run can continue.',
  replacement_staff_takeover: 'Staff took over this task.',
};

const holdReason = (reason: string | null) => (reason && REASON_LABELS[reason]) ||
  'This run needs attention. Ask an administrator to inspect the recorded reason.';

export const AutomationRunDrawer: React.FC<{
  runId: string; onClose: () => void; onChanged?: () => void;
  initialDetail?: RunDetail; loadDetail?: () => Promise<RunDetail>; readOnly?: boolean;
}> = ({ runId, onClose, onChanged, initialDetail, loadDetail, readOnly = false }) => {
  const [run, setRun] = useState<RunDetail | null>(initialDetail || null);
  const [problem, setProblem] = useState('');
  const [notice, setNotice] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [contactAt, setContactAt] = useState('');
  const [channel, setChannel] = useState('phone');
  const contactKey = useRef<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const load = useCallback(() => {
    let active = true;
    (loadDetail ? loadDetail() : getReplacementRun(runId)).then((value) => {
      if (active) { setRun(value); setProblem(''); setUncertain(false); }
    }, () => { if (active) setProblem("Couldn't load this run. Try refreshing."); });
    return () => { active = false; };
  }, [loadDetail, runId]);
  useEffect(() => load(), [load, generation]);
  const refresh = () => { setGeneration((value) => value + 1); setNotice(''); };
  const act = async (action: 'pause' | 'resume' | 'check-sent' | 'send-again' | 'dealt-with') => {
    if (!run || readOnly || !run.allowed_actions.includes(action) || busy || uncertain ||
      (action === 'send-again' && ['unknown_external_outcome', 'pending_reconciliation'].includes(run.status))) return;
    setBusy(true); setProblem(''); setNotice('');
    const result = await runSave(() => controlReplacementRun(runId, action,
      { expected_revision: run.revision, ...(note.trim() ? { note: note.trim() } : {}) }));
    setBusy(false);
    if (!result.ok) {
      setProblem(result.message);
      setUncertain(true);
      return;
    }
    if (result.value) setRun(result.value);
    setNote('');
    setNotice(action === 'check-sent' ? 'The check is pending until refreshed evidence confirms the outcome.'
      : action === 'dealt-with' ? 'Attention acknowledged. This does not complete the task or prove delivery.' : 'Saved.');
    onChanged?.();
  };
  const contact = async () => {
    if (!run || readOnly || !run.allowed_actions.includes('contact') || !contactAt || busy || uncertain) return;
    const received = new Date(contactAt);
    if (!Number.isFinite(received.getTime()) || received.getTime() > Date.now()) {
      setProblem('Enter a valid contact time that is not in the future.'); return;
    }
    if (!contactKey.current) contactKey.current = crypto.randomUUID();
    setBusy(true); setProblem('');
    const result = await runSave(() => recordReplacementContact(runId, {
      received_at: received.toISOString(), channel, idempotency_key: contactKey.current!,
      ...(note.trim() ? { note: note.trim() } : {}),
    }));
    setBusy(false);
    if (!result.ok) { setProblem(result.message); setUncertain(true); return; }
    contactKey.current = null;
    if (result.value) setRun(result.value);
    setNotice('Contact recorded. Refresh to check the current run.');
    onChanged?.();
  };
  const can = (action: string) => !readOnly && Boolean(run?.allowed_actions.includes(action)) && !busy && !uncertain &&
    !(action === 'send-again' && ['unknown_external_outcome', 'pending_reconciliation'].includes(run?.status || ''));
  // Final review C8: "Put on list" only for a run whose saved steps carry a list (a lists-aware
  // backend always writes one on its manual ending); an older run's ending is "Mark as manual".
  const onList = (run?.plan?.cards || []).some((card) => card.kind === 'manual' && card.worklist !== undefined);
  const stepLabel = (nodeId: string | null | undefined) => {
    const kind = (run?.steps || []).find((step) => step.id === nodeId)?.kind || '';
    return kind === 'mark_manual' && onList ? 'Put on list' : STEP_LABELS[kind] || 'Workflow step';
  };
  return <Drawer eyebrow="Workflow run" title={run?.workflow?.name || 'Workflow run'}
    subtitle={run ? `Version ${run.workflow?.version ?? run.workflow_version} · ${detailLabel(run)}` : undefined}
    onClose={onClose} busy={busy} footer={<button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>}>
    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={refresh} disabled={busy}>Refresh evidence</button>
    {problem && <p role="alert">{problem} {isClash(problem) || uncertain ? 'Refresh evidence before another action.' : ''}</p>}
    {notice && <p role="status">{notice}</p>}
    {!run && !problem && <p>Loading…</p>}
    {run && <div className="cur-stack">
      <p>{run.source?.label || 'Approved example'} · {run.ending ? `Actual ending: ${run.ending}` : 'No ending recorded yet'}</p>
      {run.plan && <section aria-label="Frozen workflow plan">
        <h3>Steps saved with this decision</h3><StepChips steps={run.plan.cards || []} />
        <p>Proposed ending: {run.plan.ending || 'Not set'}. An ending is only actual after the run finishes.</p>
      </section>}
      {['held', 'failed', 'unknown_external_outcome', 'pending_reconciliation'].includes(run.status) &&
        <section aria-label="Current run issue">
          <h3>Why it needs attention</h3>
          <p>{holdReason(run.reason_code)}</p>
          <p>Current step: {stepLabel(run.node_id)}</p>
        </section>}
      {run.status === 'unknown_external_outcome' || run.status === 'pending_reconciliation'
        ? <p className="cur-callout cur-callout--warn">The send outcome is unresolved. Do not send again without proof.</p> : null}
      {run.workflow_paused && <p>Workflow processing is paused.</p>}
      {run.events.length > 0 && <section><h3>History</h3><ol>{run.events.map((event) =>
        <li key={event.event_id}>{event.node_id ? `${stepLabel(event.node_id)} · ` : ''}{event.kind.replace(/_/g, ' ')} · {new Date(event.created_at).toLocaleString('en-GB')}</li>)}</ol></section>}
      {run.effects.length > 0 && <section><h3>Recorded effects</h3><ul>{run.effects.map((effect) =>
        <li key={effect.effect_id}>{stepLabel(effect.node_id)} · {effect.status.replace(/_/g, ' ')}</li>)}</ul></section>}
      {!readOnly && <><label>Note (optional)<textarea value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} maxLength={2000} /></label>
      <div className="cur-inline">
        {(['pause', 'resume', 'check-sent', 'send-again', 'dealt-with'] as const).map((action) =>
          <button key={action} type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={!can(action)}
            onClick={() => { void act(action); }} title={action === 'send-again' && !can(action) ? 'The server has not proved another send is safe.' : undefined}>
            {{ pause: 'Pause', resume: 'Resume', 'check-sent': 'Check sent', 'send-again': 'Send again', 'dealt-with': 'Dealt with' }[action]}
          </button>)}
      </div>
      {run.allowed_actions.includes('contact') && <section aria-label="Record staff contact">
        <h3>Record contact</h3>
        <label>Received at<input type="datetime-local" value={contactAt} onChange={(event) => { setContactAt(event.target.value); contactKey.current = null; }} /></label>
        <label>Channel<select value={channel} onChange={(event) => { setChannel(event.target.value); contactKey.current = null; }}>
          <option value="phone">Phone</option><option value="email">Email</option><option value="offline">Offline</option><option value="other">Other</option>
        </select></label>
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={!contactAt || busy || uncertain} onClick={() => { void contact(); }}>Record contact</button>
      </section>}</>}
    </div>}
  </Drawer>;
};
